import React from "react";
import { Button, Modal, Divider } from "@mantine/core";
import { FaRegClock } from "react-icons/fa";
import { CiLocationOn } from "react-icons/ci";
import { GrStatusPlaceholder } from "react-icons/gr";
import { GrMapLocation } from "react-icons/gr";
import { useMemo, useEffect } from "react";
import { GoLocation } from "react-icons/go";
import AddressIcon from "../svg/addressIcon";
import FloorIcon from "../svg/floorIcon";
import { HiLocationMarker } from "react-icons/hi";
import { findObjDifference, actionColor } from "../utils/general";
const DetailModal = ({
  setOpenDetailModal,
  openDetailModal,
  logsDetail,
  locations,
}) => {
  const newSiteWorkingHours = useMemo(() => {
    return logsDetail?.type === "location" && logsDetail?.newValue?.openingTime
      ? Object.entries(logsDetail.newValue.openingTime)
          .map(([day, times]) => {
            if (times) {
              const [startTime, endTime] = (times as string).split("-");
              const formattedStartTime = startTime && `${startTime.trim()}`;
              const formattedEndTime = endTime && `${endTime.trim()}`;
              return {
                day: day.slice(0, 3),
                time: `${formattedStartTime} - ${formattedEndTime}`,
              };
            }
            return null;
          })
          .filter((el) => el !== null)
      : [];
  }, [logsDetail?.newValue?.openingTime]);

  const oldSiteWorkingHours = useMemo(() => {
    return logsDetail?.type === "location" && logsDetail?.oldValue?.openingTime
      ? Object.entries(logsDetail?.oldValue?.openingTime)
          .map(([day, times]) => {
            if (times) {
              const [startTime, endTime] = (times as string).split("-");
              const formattedStartTime = startTime && `${startTime.trim()}`;
              const formattedEndTime = endTime && `${endTime.trim()}`;
              return {
                day: day.slice(0, 3),
                time: `${formattedStartTime} - ${formattedEndTime}`,
              };
            }
            return null;
          })
          .filter((el) => el !== null)
      : [];
  }, [logsDetail?.oldValue?.openingTime]);

  const getUpdateDifference = useMemo(() => {
    return logsDetail?.oldValue && logsDetail?.newValue
      ? findObjDifference(logsDetail.oldValue, logsDetail.newValue)
      : [];
  }, [logsDetail?.oldValue, logsDetail?.newValue]);

  const displayData = () => {
    switch (logsDetail.action) {
      case "create":
        return logsDetail.type === "location" ? (
          <Site
            detail={logsDetail.newValue}
            SiteWorkingHours={newSiteWorkingHours}
          />
        ) : (
          <Floor detail={logsDetail.newValue} />
        );

      case "update":
        return logsDetail.type === "location" ? (
          <div>
            <GridItemSite
              title="Affected Site"
              content={logsDetail.targetName}
            />
            {getUpdateDifference.map((el, index) => {
              return (
                <UpdateDifference
                  key={index}
                  el={el}
                  oldValue={logsDetail.oldValue[el]}
                  newValue={logsDetail.newValue[el]}
                  oldSiteWorkingHours={oldSiteWorkingHours}
                  newSiteWorkingHours={newSiteWorkingHours}
                />
              );
            })}
          </div>
        ) : logsDetail.type === "floor" ? (
          <div>
            <div className="flex flex-col gap-2">
              <GridItemSite
                title="Affected Site"
                content={logsDetail.parentName}
              />
              <GridItemSite
                title="Affected Floor"
                content={logsDetail.targetName}
              />
            </div>

            {getUpdateDifference.map((el, index) => {
              return (
                <UpdateDifference
                  key={index}
                  el={el}
                  oldValue={logsDetail.oldValue[el]}
                  newValue={logsDetail.newValue[el]}
                  oldSiteWorkingHours={oldSiteWorkingHours}
                  newSiteWorkingHours={newSiteWorkingHours}
                />
              );
            })}
          </div>
        ) : (
          <div>
            <div className="flex flex-col gap-2 mb-4">
              <GridItemSite title="Affected Site" content={"site name"} />
              <GridItemSite
                title="Affected Floor"
                content={logsDetail.parentName}
              />
              <GridItemSite
                title="Affected Desk"
                content={logsDetail.targetName}
              />
            </div>
            <StatusBox
              status={logsDetail.oldValue}
              borderColor="#d67976"
              bgColor="bg-[#fcecee]"
              fallBackStatus=""
            />
            <StatusBox
              status={logsDetail.newValue}
              borderColor="#91c58a"
              bgColor="bg-[#eaf5ea]"
              fallBackStatus=""
            />
          </div>
        );
      case "status change":
        return (
          <div>
            <div className="flex flex-col gap-2 mb-4">
              <GridItemSite title="Affected Site" content={"site name"} />
              <GridItemSite
                title="Affected Floor"
                content={logsDetail.parentName}
              />
              <GridItemSite
                title="Affected Desk"
                content={logsDetail.targetName}
              />
            </div>

            <div>
              <StatusBox
                status={logsDetail.oldValue}
                borderColor="#d67976"
                bgColor="bg-[#fcecee]"
                fallBackStatus="No Status"
              />
              <StatusBox
                status={logsDetail.newValue}
                borderColor="#91c58a"
                bgColor="bg-[#eaf5ea]"
                fallBackStatus="No Status"
              />
            </div>
          </div>
        );
      case "sensor unassignment":
        return (
          <div>
            <div className="flex flex-col gap-2 mb-4">
              <GridItemSite title="Affected Site" content={"site name"} />
              <GridItemSite
                title="Affected Floor"
                content={logsDetail.parentName}
              />
              <GridItemSite
                title="Affected Desk"
                content={logsDetail.targetName}
              />
            </div>

            <div>
              <StatusBox
                status={logsDetail.oldValue}
                borderColor="#d67976"
                bgColor="bg-[#fcecee]"
                fallBackStatus="No Sensor"
              />
              <StatusBox
                status={logsDetail.newValue}
                borderColor="#91c58a"
                bgColor="bg-[#eaf5ea]"
                fallBackStatus="No Sensor"
              />
            </div>
          </div>
        );
      case "sensor assignment":
        return (
          <div>
            <div className="flex flex-col gap-2 mb-4">
              <GridItemSite title="Affected Site" content={"site name"} />
              <GridItemSite
                title="Affected Floor"
                content={logsDetail.parentName}
              />
              <GridItemSite
                title="Affected Desk"
                content={logsDetail.targetName}
              />
            </div>

            <div>
              <StatusBox
                status={logsDetail.oldValue}
                borderColor="#d67976"
                bgColor="bg-[#fcecee]"
                fallBackStatus="No Sensor"
              />
              <StatusBox
                status={logsDetail.newValue}
                borderColor="#91c58a"
                bgColor="bg-[#eaf5ea]"
                fallBackStatus="No Sensor"
              />
            </div>
          </div>
        );
      case "delete":
        return logsDetail.type === "location" ? (
          <Site
            detail={logsDetail.oldValue}
            SiteWorkingHours={oldSiteWorkingHours}
          />
        ) : (
          <Floor detail={logsDetail.oldValue} />
        );
      case "live":
        return (
          <div>
            <div className="flex flex-col gap-2 mb-4">
              <GridItemSite
                title="Affected Site"
                content={logsDetail.parentName}
              />
              <GridItemSite
                title="Affected Floor"
                content={logsDetail.targetName}
              />
            </div>
            {getUpdateDifference.map((el, index) => {
              return (
                <UpdateDifference
                  key={index}
                  el={el}
                  oldValue={logsDetail.oldValue[el]}
                  newValue={logsDetail.newValue[el]}
                  oldSiteWorkingHours={oldSiteWorkingHours}
                  newSiteWorkingHours={newSiteWorkingHours}
                />
              );
            })}
          </div>
        );

      default:
        return <div> not sure </div>;
    }
  };
  const displayTitle = () => {};

  return (
    <Modal
      opened={openDetailModal}
      onClose={() => setOpenDetailModal(false)}
      size="md"
      withCloseButton={false}
      radius={"lg"}
      padding={0}
    >
      {logsDetail ? (
        <div className="my-5">
          <div className="flex justify-end  mx-6">
            {/* <h1>title</h1> */}
            <div
              style={{
                background: actionColor(logsDetail.action).bgClr,
                color: actionColor(logsDetail.action).clr,
                border: `1px solid ${actionColor(logsDetail.action).clr}`,
              }}
              className={`rounded-[100px] px-3 py-[2px] w-fit text-md flex gap-2 items-center capitalize `}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  background: actionColor(logsDetail.action).clr,
                }}
              />
              {logsDetail.action}
            </div>
          </div>
          <Divider my="lg" size={"xs"} color="#e8ebee" />
          <div className="mx-6">{displayData()}</div>
        </div>
      ) : (
        <p>no details</p>
      )}
      <Divider my="md" size={"xs"} color="#e8ebee" />
      <div className="flex justify-end mx-6 mb-5  ">
        <Button
          size="md"
          color="#4D5F7A"
          variant="outline"
          onClick={() => setOpenDetailModal(false)}
        >
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default DetailModal;

const Site = ({ detail, SiteWorkingHours }) => {
  return (
    <div className="flex flex-col gap-4 ">
      <GridItemSite title="Name" content={detail.name} />
      <GridItemSite title="Description" content={detail.description} />
      <GridItemSite title="Address" content={detail.address} />
      <GridItemSite title="Status" content={detail.live ? "Live" : "Draft"} />
      <GridItemSite
        title="Operating Hours"
        content={
          SiteWorkingHours.length > 0
            ? SiteWorkingHours.map((el, index) => (
                <p key={index} className="text-md">
                  {`${el.day} ${el.time}`}
                </p>
              ))
            : "Everyday, 09:00AM - 05:00PM"
        }
      />
    </div>
  );
};
const Floor = ({ detail }) => {
  const amenitiesContent =
    detail.amenities?.length > 0 ? (
      detail.amenities.map((el, index) => (
        <span key={el} className="text-base">
          {el}
          {detail.amenities.length - 1 !== index && ", "}
        </span>
      ))
    ) : (
      <span className="text-base">None</span>
    );
  return (
    <div className="flex flex-col gap-4">
      <GridItemSite
        title="Site Name"
        content={detail.parentName || "site name"}
      />
      <GridItemSite title="Floor Name" content={detail.name} />

      <GridItemSite title="Position" content={detail.position} />

      <GridItemSite title="Status" content={detail.live ? "Live" : "Draft"} />
      <GridItemSite title="Amenities" content={amenitiesContent} />
    </div>
  );
};
const GridItemSite = ({ title, content }) => {
  return (
    <div className=" gap-10 items-start flex justify-between">
      <h2 className={`font-normal text-base capitalize   text-grayText3 `}>
        {title}
      </h2>
      <div className="text-base ">{content}</div>
    </div>
  );
};
const UpdateDifference = ({
  el,
  oldValue,
  newValue,
  oldSiteWorkingHours,
  newSiteWorkingHours,
}) => {
  const isOpeningTime = el.includes("openingTime");
  const isAmenities = el.includes("amenities");
  const isLive = el.includes("live");

  const renderOldValue = () => {
    if (isOpeningTime) {
      return oldSiteWorkingHours.map((hour, index) => (
        <p key={index} className="text-md flex-1">
          {`${hour.day} ${hour.time}`}
        </p>
      ));
    } else if (isAmenities) {
      return oldValue.amenities?.map((amenity, index) => (
        <div className="text-black text-sm" key={index}>
          {amenity}
        </div>
      ));
    } else if (isLive) {
      return oldValue ? (
        <span className="text-sm">Live</span>
      ) : (
        <span className="text-sm">Draft</span>
      );
    } else {
      return <span className="text-sm">{oldValue}</span>;
    }
  };

  const renderNewValue = () => {
    if (isOpeningTime) {
      return newSiteWorkingHours.map((hour, index) => (
        <p key={index} className="text-md flex-1">
          {`${hour.day} ${hour.time}`}
        </p>
      ));
    } else if (isAmenities) {
      return newValue.amenities?.map((amenity, index) => (
        <div className="text-black text-sm" key={index}>
          {amenity}
        </div>
      ));
    } else if (isLive) {
      return newValue ? (
        <span className="text-sm">Live</span>
      ) : (
        <span className="text-sm">Draft</span>
      );
    } else {
      return <span className="text-sm">{newValue}</span>;
    }
  };

  return (
    <div className=" " key={el}>
      <p className="capitalize mt-4 mb-2 text-sm font-normal text-grayText3">
        {isOpeningTime
          ? "Opening Time"
          : isLive
          ? "Site Status"
          : isAmenities
          ? "Amenities"
          : el}
      </p>
      {(oldValue || isLive || oldValue === 0) && (
        <div
          className="bg-[#fcecee] p-2 mb-2 border-left rounded-md"
          style={{ borderColor: "#d67976" }}
        >
          {renderOldValue()}
        </div>
      )}
      {(newValue || isLive || newValue === 0) && (
        <div
          className="bg-[#eaf5ea] p-2 border-left rounded-md"
          style={{ borderColor: "#91c58a" }}
        >
          {renderNewValue()}
        </div>
      )}
    </div>
  );
};
const StatusBox = ({ status, fallBackStatus, borderColor, bgColor }) => {
  return (
    <div
      className={`p-2 mb-2 border-left text-sm rounded-md ${bgColor}`}
      style={{ borderColor }}
    >
      {status ? status : fallBackStatus}
    </div>
  );
};
